<script lang="ts">
  // IMPORTANT: Svelte 5에서는 수동 레이아웃 래핑이 필요합니다
  import { router } from '@inertiajs/svelte';

  interface ThreadsPost {
    id: string;
    threadsId: string;
    content: string;
    authorUsername: string;
    authorDisplayName: string | null;
    authorAvatarUrl: string | null;
    likesCount: number;
    repliesCount: number;
    repostsCount: number;
    quotesCount: number;
    postedAt: string;
    mediaUrls: string[];
    mediaType: string | null;
    fetchStatus: string;
    fetchError: string | null;
    lastFetchedAt: string | null;
    threadsUrl: string;
    metadata: Record<string, any>;
    createdAt: string;
    updatedAt: string;
    statusBadge: {
      text: string;
      color: string;
      icon: string;
    };
    engagementSummary: {
      total: number;
      likes: number;
      replies: number;
      reposts: number;
      quotes: number;
    };
  }

  let { post } = $props<{ post: ThreadsPost }>();

  function getBadgeClass(color: string): string {
    const classes: Record<string, string> = {
      success: 'badge-success',
      error: 'badge-error',
      warning: 'badge-warning',
      default: 'badge-neutral'
    };
    return classes[color] || 'badge-neutral';
  }

  function deletePost() {
    if (confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
      router.delete(`/admin/threads_posts/${post.id}`);
    }
  }
</script>

<div class="container mx-auto px-4 py-8 max-w-4xl">
    <!-- Breadcrumb -->
    <div class="breadcrumbs text-sm mb-4">
      <ul>
        <li><a href="/admin">Admin</a></li>
        <li><a href="/admin/threads_posts">Threads 게시글</a></li>
        <li>@{post.author_username}</li>
      </ul>
    </div>

    <!-- Post Card -->
    <div class="card bg-base-100 shadow-lg border border-base-300">
      <!-- Author Header -->
      <div class="card-body">
        <div class="flex items-center gap-4 mb-4">
          {#if post.author_avatar_url}
            <img
              src={post.author_avatar_url}
              alt={post.author_username}
              class="w-16 h-16 rounded-full"
            />
          {/if}
          <div class="flex-1">
            <h2 class="text-xl font-bold">@{post.author_username}</h2>
            {#if post.author_display_name}
              <p class="text-content-secondary">{post.author_display_name}</p>
            {/if}
            <div class="flex items-center gap-2 mt-1">
              <span class="text-sm text-gray-500">
                {new Date(post.posted_at).toLocaleString('ko-KR')}
              </span>
              <span class="badge {getBadgeClass(post.status_badge.color)} badge-sm">
                {post.status_badge.text}
              </span>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="prose dark:prose-invert max-w-none mb-6">
          <p class="whitespace-pre-wrap">{post.content}</p>
        </div>

        <!-- Media -->
        {#if post.media_urls && post.media_urls.length > 0}
          <div class="grid grid-cols-2 gap-4 mb-6">
            {#each post.media_urls as url}
              <img
                src={url}
                alt="Post media"
                class="rounded-lg w-full object-cover"
              />
            {/each}
          </div>
        {/if}

        <!-- Engagement Stats -->
        <div class="grid grid-cols-4 gap-4 mb-6">
          <div class="stat bg-base-200 rounded-lg p-4">
            <div class="stat-title text-sm">좋아요</div>
            <div class="stat-value text-2xl">{post.likes_count}</div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-4">
            <div class="stat-title text-sm">댓글</div>
            <div class="stat-value text-2xl">{post.replies_count}</div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-4">
            <div class="stat-title text-sm">리포스트</div>
            <div class="stat-value text-2xl">{post.reposts_count}</div>
          </div>
          <div class="stat bg-base-200 rounded-lg p-4">
            <div class="stat-title text-sm">인용</div>
            <div class="stat-value text-2xl">{post.quotes_count}</div>
          </div>
        </div>

        <!-- Technical Details -->
        <div class="divider">기술 정보</div>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-semibold">Threads ID:</span>
            <code class="ml-2">{post.threads_id}</code>
          </div>
          <div>
            <span class="font-semibold">상태:</span>
            <span class="ml-2">{post.fetch_status}</span>
          </div>
          <div>
            <span class="font-semibold">마지막 가져오기:</span>
            <span class="ml-2">
              {post.last_fetched_at ? new Date(post.last_fetched_at).toLocaleString('ko-KR') : 'N/A'}
            </span>
          </div>
          <div>
            <span class="font-semibold">미디어 타입:</span>
            <span class="ml-2">{post.media_type || 'N/A'}</span>
          </div>
        </div>

        <!-- Error Display -->
        {#if post.fetch_error}
          <div class="alert alert-error mt-4">
            <span class="font-semibold">에러:</span>
            <span>{post.fetch_error}</span>
          </div>
        {/if}

        <!-- Actions -->
        <div class="card-actions justify-end mt-6 gap-2">
          <a
            href={post.threads_url}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-primary"
          >
            Threads에서 보기
          </a>
          <a
            href="/admin/threads_posts"
            class="btn btn-ghost"
          >
            목록으로
          </a>
          <button
            onclick={deletePost}
            class="btn btn-error"
          >
            삭제
          </button>
        </div>
      </div>
    </div>

    <!-- Metadata (Collapsible) -->
    {#if post.metadata && Object.keys(post.metadata).length > 0}
      <div class="collapse collapse-arrow bg-base-200 mt-4">
        <input type="checkbox" />
        <div class="collapse-title font-medium">
          메타데이터 (개발자용)
        </div>
        <div class="collapse-content">
          <pre class="text-xs overflow-x-auto">{JSON.stringify(post.metadata, null, 2)}</pre>
        </div>
      </div>
    {/if}
  </div>
